import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ProtectedRoute, PublicOnlyRoute, AdminRoute, KycStepRoute } from '@/components/common/ProtectedRoute';
import { AuthProvider } from '@/contexts/AuthContext';
import { DevNav } from '@/components/common/DevNav';
import { publicConfig } from '@/config/public-config';

// Layouts
import { PublicLayout }    from '@/components/layout/PublicLayout/PublicLayout';
import { AuthLayout }      from '@/components/layout/AuthLayout/AuthLayout';

const LandingPage = lazy(() => import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })));
const PublicInfoPage = lazy(() => import('@/pages/PublicInfoPage').then(m => ({ default: m.PublicInfoPage })));
const GreenCubeOfferPage = lazy(() => import('@/pages/offers/GreenCubeOfferPage').then(m => ({ default: m.GreenCubeOfferPage })));
const InvitationAcceptPage = lazy(() => import('@/pages/invitations/InvitationAcceptPage').then(m => ({ default: m.InvitationAcceptPage })));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage').then(m => ({ default: m.SignupPage })));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const KycPersonalInfoPage = lazy(() => import('@/pages/kyc/KycPersonalInfoPage').then(m => ({ default: m.KycPersonalInfoPage })));
const KycIdentityPage = lazy(() => import('@/pages/kyc/KycIdentityPage').then(m => ({ default: m.KycIdentityPage })));
const KycSelfiePage = lazy(() => import('@/pages/kyc/KycSelfiePage').then(m => ({ default: m.KycSelfiePage })));
const KycProofOfAddressPage = lazy(() => import('@/pages/kyc/KycProofOfAddressPage').then(m => ({ default: m.KycProofOfAddressPage })));
const KycSourceOfFundsPage = lazy(() => import('@/pages/kyc/KycSourceOfFundsPage').then(m => ({ default: m.KycSourceOfFundsPage })));
const KycReviewPage = lazy(() => import('@/pages/kyc/KycReviewPage').then(m => ({ default: m.KycReviewPage })));
const WalletTypeChoicePage = lazy(() => import('@/pages/wallet/WalletTypeChoicePage').then(m => ({ default: m.WalletTypeChoicePage })));
const WalletCreatedPage = lazy(() => import('@/pages/wallet/WalletCreatedPage').then(m => ({ default: m.WalletCreatedPage })));
const MarketplacePage = lazy(() => import('@/pages/marketplace/MarketplacePage').then(m => ({ default: m.MarketplacePage })));
const AssetDetailPage = lazy(() => import('@/pages/marketplace/AssetDetailPage').then(m => ({ default: m.AssetDetailPage })));
const InvestAmountPage = lazy(() => import('@/pages/invest/InvestAmountPage').then(m => ({ default: m.InvestAmountPage })));
const InvestPaymentPage = lazy(() => import('@/pages/invest/InvestPaymentPage').then(m => ({ default: m.InvestPaymentPage })));
const InvestSummaryPage = lazy(() => import('@/pages/invest/InvestSummaryPage').then(m => ({ default: m.InvestSummaryPage })));
const InvestConfirmationPage = lazy(() => import('@/pages/invest/InvestConfirmationPage').then(m => ({ default: m.InvestConfirmationPage })));
const InvestSuccessPage = lazy(() => import('@/pages/invest/InvestSuccessPage').then(m => ({ default: m.InvestSuccessPage })));
const PaymentSuccessPage = lazy(() => import('@/pages/payment/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import('@/pages/payment/PaymentCancelPage').then(m => ({ default: m.PaymentCancelPage })));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PortfolioPage = lazy(() => import('@/pages/dashboard/PortfolioPage').then(m => ({ default: m.PortfolioPage })));
const YieldPage = lazy(() => import('@/pages/dashboard/YieldPage').then(m => ({ default: m.YieldPage })));
const TransfersPage = lazy(() => import('@/pages/dashboard/TransfersPage').then(m => ({ default: m.TransfersPage })));
const SubscriptionsPage = lazy(() => import('@/pages/subscriptions/SubscriptionsPage').then(m => ({ default: m.SubscriptionsPage })));
const SubscriptionSuccessPage = lazy(() => import('@/pages/subscriptions/SubscriptionSuccessPage').then(m => ({ default: m.SubscriptionSuccessPage })));
const MySubscriptionPage = lazy(() => import('@/pages/subscriptions/MySubscriptionPage').then(m => ({ default: m.MySubscriptionPage })));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage').then(m => ({ default: m.ProfilePage })));
const AssetOfferWizardPage = lazy(() => import('@/features/solar-asset-offer/components/AssetOfferWizardPage').then(m => ({ default: m.AssetOfferWizardPage })));
const AdminEventJournalPage = lazy(() => import('@/pages/admin/AdminEventJournalPage').then(m => ({ default: m.AdminEventJournalPage })));
const DocumentFactoryReadinessPage = lazy(() => import('@/pages/admin/DocumentFactoryReadinessPage').then(m => ({ default: m.DocumentFactoryReadinessPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const InvestorAppLayout = lazy(() => import('@/features/investor-portal/components/PortalUi').then(m => ({ default: m.InvestorAppLayout })));
const PortalDashboardPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalDashboardPage })));
const PortalProgramsPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalProgramsPage })));
const PortalInvestorsPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalInvestorsPage })));
const PortalSubscriptionsReadPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalSubscriptionsPage })));
const PortalLoanPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalLoanPage })));
const PortalPaymentsPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalPaymentsPage })));
const PortalGovernancePage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalGovernancePage })));
const PortalConversionProgressPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalConversionProgressPage })));
const PortalConversionCampaignPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalConversionCampaignPage })));
const PortalConversionResultPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalConversionResultPage })));
const PortalSharesPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalSharesPage })));
const PortalDocumentsPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalDocumentsPage })));
const PortalDocumentTemplatesPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalDocumentTemplatesPage })));
const PortalDocumentExportsPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalDocumentExportsPage })));
const PortalActivityPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalActivityPage })));
const PortalAuditPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalAuditPage })));
const PortalExceptionsPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalExceptionsPage })));
const PortalReadModelsPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalReadModelsPage })));
const PortalNotificationDeadLettersPage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalNotificationDeadLettersPage })));
const PortalProfilePage = lazy(() => import('@/features/investor-portal/pages/PortalPages').then(m => ({ default: m.PortalProfilePage })));
const PortalNotificationsPage = lazy(() => import('@/features/notifications/NotificationPages').then(m => ({ default: m.PortalNotificationsPage })));
const PortalNotificationPreferencesPage = lazy(() => import('@/features/notifications/NotificationPages').then(m => ({ default: m.PortalNotificationPreferencesPage })));
const AdminConsoleLayout = lazy(() => import('@/features/admin-console/components/AdminConsoleUi').then(m => ({ default: m.AdminConsoleLayout })));
const AdminDashboardPageP44 = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDashboardPage })));
const AdminProgramsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminProgramsPage })));
const AdminInvestorsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminInvestorsPage })));
const AdminInvestmentsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminInvestmentsPage })));
const AdminPaymentsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminPaymentsPage })));
const AdminSettlementsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminSettlementsPage })));
const AdminDebtLotsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDebtLotsPage })));
const AdminDebtPositionsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDebtPositionsPage })));
const AdminDebtLedgerPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDebtLedgerPage })));
const AdminInterestPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminInterestPage })));
const AdminGovernanceMeetingsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminGovernanceMeetingsPage })));
const AdminGovernanceResolutionsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminGovernanceResolutionsPage })));
const AdminConversionsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminConversionsPage })));
const AdminEquityPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminEquityPage })));
const AdminEquityRegisterPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminEquityRegisterPage })));
const AdminDocumentsPageP44 = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDocumentsPage })));
const AdminDocumentTemplatesPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDocumentTemplatesPage })));
const AdminDocumentCategoriesPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDocumentCategoriesPage })));
const AdminDocumentAuditPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDocumentAuditPage })));
const AdminDocumentExportsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminDocumentExportsPage })));
const AdminNotificationsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminNotificationsPage })));
const AdminNotificationAttemptsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminNotificationAttemptsPage })));
const AdminNotificationDeadLettersPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminNotificationDeadLettersPage })));
const AdminNotificationTemplatesPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminNotificationTemplatesPage })));
const AdminNotificationHealthPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminNotificationHealthPage })));
const AdminAuditPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminAuditPage })));
const AdminExceptionsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminExceptionsPage })));
const AdminReadModelsPage = lazy(() => import('@/features/admin-console/pages/AdminConsolePages').then(m => ({ default: m.AdminReadModelsPage })));

const devInvestmentRoutes = import.meta.env.DEV
  ? [
    {
      path: '/investissement/:assetId',
      element: <ProtectedRoute requireKyc requireWallet><DevInvestmentPage /></ProtectedRoute>,
    },
    {
      path: '/investissement',
      element: <ProtectedRoute requireKyc requireWallet><DevInvestmentPage /></ProtectedRoute>,
    },
  ]
  : [];

function DevInvestmentPage() {
  const InvestmentPage = lazy(() => import('@/features/investment/pages/InvestmentPage').then(m => ({ default: m.InvestmentPage })));
  return <InvestmentPage />;
}

function PageLoader({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="min-h-screen bg-slate-50" />}>{children}</Suspense>;
}

const router = createBrowserRouter([
  {
    element: (
      <GoogleOAuthProvider clientId={publicConfig.GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <Outlet />
          {import.meta.env.DEV ? <DevNav /> : null}
        </AuthProvider>
      </GoogleOAuthProvider>
    ),
    children: [
  // ── Landing (public) ────────────────────────────────────────────────────────
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/comment-ca-marche', element: <PublicInfoPage page="how-it-works" /> },
      { path: '/rendements', element: <PublicInfoPage page="returns" /> },
      { path: '/a-propos', element: <PublicInfoPage page="about" /> },
      { path: '/conditions', element: <PublicInfoPage page="terms" /> },
      { path: '/offers/:offerUuid', element: <GreenCubeOfferPage /> },
      { path: '/invitations/:inviteUuid', element: <InvitationAcceptPage /> },
      { path: '/confidentialite', element: <PublicInfoPage page="privacy" /> },
      { path: '/politique-de-confidentialite', element: <PublicInfoPage page="privacy" /> },
      { path: '/risques', element: <PublicInfoPage page="risks" /> },
      { path: '/contact', element: <PublicInfoPage page="contact" /> },
    ],
  },

  // Auth publique (redirige si déjà connecté)
  {
    path: '/inscription',
    element: <PublicOnlyRoute><SignupPage /></PublicOnlyRoute>,
  },
  {
    path: '/connexion',
    element: <PublicOnlyRoute><LoginPage /></PublicOnlyRoute>,
  },
  {
    path: '/mot-de-passe-oublie',
    element: <ForgotPasswordPage />,
  },
  // Vérification email (accessible authentifié ou non — compte pending)
  {
    path: '/verifier-email',
    element: <VerifyEmailPage />,
  },

  // ── Marketplace (public, enrichi si connecté) ────────────────────────────────
  {
    element: <AuthLayout />,
    children: [
      { path: '/actifs',          element: <MarketplacePage /> },
      { path: '/actifs/:assetId', element: <AssetDetailPage /> },
      { path: '/investir',        element: <MarketplacePage mode="invest" /> },
    ],
  },

  // ── KYC (authentifié) ────────────────────────────────────────────────────────
  {
    path: '/kyc/informations',
    element: <ProtectedRoute><Navigate to="/kyc/informations-personnelles" replace /></ProtectedRoute>,
  },
  {
    path: '/kyc/informations-personnelles',
    element: <ProtectedRoute><KycStepRoute stepIndex={0}><KycPersonalInfoPage /></KycStepRoute></ProtectedRoute>,
  },
  {
    path: '/kyc/identite',
    element: <ProtectedRoute><KycStepRoute stepIndex={1}><KycIdentityPage /></KycStepRoute></ProtectedRoute>,
  },
  {
    path: '/kyc/selfie',
    element: <ProtectedRoute><KycStepRoute stepIndex={2}><KycSelfiePage /></KycStepRoute></ProtectedRoute>,
  },
  {
    path: '/kyc/justificatif-domicile',
    element: <ProtectedRoute><KycStepRoute stepIndex={3}><KycProofOfAddressPage /></KycStepRoute></ProtectedRoute>,
  },
  {
    path: '/kyc/source-des-fonds',
    element: <ProtectedRoute><KycStepRoute stepIndex={4}><KycSourceOfFundsPage /></KycStepRoute></ProtectedRoute>,
  },
  {
    path: '/kyc/revue',
    element: <ProtectedRoute><KycStepRoute stepIndex={5}><KycReviewPage /></KycStepRoute></ProtectedRoute>,
  },

  // ── Wallet (authentifié) ─────────────────────────────────────────────────────
  {
    path: '/wallet/creation',
    element: <ProtectedRoute><WalletTypeChoicePage /></ProtectedRoute>,
  },
  {
    path: '/wallet/cree',
    element: <ProtectedRoute><WalletCreatedPage /></ProtectedRoute>,
  },

  // ── Espace compte accessible dès l'inscription ──────────────────────────────
  {
    element: <ProtectedRoute><AuthLayout /></ProtectedRoute>,
    children: [
      { path: '/tableau-de-bord', element: <DashboardPage />  },
      { path: '/profil',          element: <ProfilePage />    },
      { path: '/parametres',      element: <ProfilePage />    },
    ],
  },

  // ── Espace financier: KYC validé et wallet actif ────────────────────────────
  {
    element: (
      <ProtectedRoute requireKyc requireWallet>
        <AuthLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '/portefeuille',    element: <PortfolioPage />  },
      { path: '/rendement',       element: <YieldPage />      },
      { path: '/transferts',      element: <TransfersPage />  },
      { path: '/abonnements',     element: <SubscriptionsPage /> },
      { path: '/abonnements/souscrire', element: <SubscriptionsPage /> },
      { path: '/abonnements/succes', element: <SubscriptionSuccessPage /> },
      { path: '/mon-abonnement',  element: <MySubscriptionPage /> },
      { path: '/mon-abonnement/historique', element: <MySubscriptionPage /> },
    ],
  },

  // ── Portail investisseur dette convertible P4.3 — read-only ────────────────
  {
    path: '/app',
    element: <ProtectedRoute><InvestorAppLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <PortalDashboardPage /> },
      { path: 'programs', element: <PortalProgramsPage /> },
      { path: 'investors', element: <PortalInvestorsPage /> },
      { path: 'subscriptions', element: <PortalSubscriptionsReadPage /> },
      { path: 'loan', element: <PortalLoanPage /> },
      { path: 'loan/lots/:lotId', element: <PortalLoanPage /> },
      { path: 'payments', element: <PortalPaymentsPage /> },
      { path: 'payments/:paymentId', element: <PortalPaymentsPage /> },
      { path: 'governance', element: <PortalGovernancePage /> },
      { path: 'conversion/progress', element: <PortalConversionProgressPage /> },
      { path: 'conversion/campaign', element: <PortalConversionCampaignPage /> },
      { path: 'conversion/result', element: <PortalConversionResultPage /> },
      { path: 'conversion/:conversionId', element: <PortalConversionProgressPage /> },
      { path: 'shares', element: <PortalSharesPage /> },
      { path: 'shares/:positionId', element: <PortalSharesPage /> },
      { path: 'documents', element: <PortalDocumentsPage /> },
      { path: 'documents/:documentId', element: <PortalDocumentsPage /> },
      { path: 'document-templates', element: <PortalDocumentTemplatesPage /> },
      { path: 'document-exports', element: <PortalDocumentExportsPage /> },
      { path: 'notifications', element: <PortalNotificationsPage /> },
      { path: 'notification-dead-letters', element: <PortalNotificationDeadLettersPage /> },
      { path: 'audit', element: <PortalAuditPage /> },
      { path: 'exceptions', element: <PortalExceptionsPage /> },
      { path: 'system/read-models', element: <PortalReadModelsPage /> },
      { path: 'activity', element: <PortalActivityPage /> },
      { path: 'profile', element: <PortalProfilePage /> },
      { path: 'profile/notifications', element: <PortalNotificationPreferencesPage /> },
    ],
  },

  // ── Tunnel investissement (KYC + wallet requis) ───────────────────────────────
  {
    path: '/investir/:assetId/montant',
    element: <ProtectedRoute requireKyc requireWallet><InvestAmountPage /></ProtectedRoute>,
  },
  {
    path: '/investir/:assetId/paiement',
    element: <ProtectedRoute requireKyc requireWallet><InvestPaymentPage /></ProtectedRoute>,
  },
  {
    path: '/investir/:assetId/resume',
    element: <ProtectedRoute requireKyc requireWallet><InvestSummaryPage /></ProtectedRoute>,
  },
  {
    path: '/investir/:assetId/confirmation',
    element: <ProtectedRoute requireKyc requireWallet><InvestConfirmationPage /></ProtectedRoute>,
  },
  {
    path: '/investir/:assetId/succes',
    element: <ProtectedRoute><InvestSuccessPage /></ProtectedRoute>,
  },

  // ── Retour de paiement Stripe Checkout ──────────────────────────────────────
  {
    path: '/payment/success',
    element: <ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>,
  },
  {
    path: '/payment/cancel',
    element: <ProtectedRoute><PaymentCancelPage /></ProtectedRoute>,
  },

  // ── Tunnel mock de développement uniquement ──────────────────────────────────
  ...devInvestmentRoutes,

  // ── WebEditor admin — publier un actif ──────────────────────────────────────
  {
    path: '/admin/publier-actif',
    element: <AdminRoute><AssetOfferWizardPage /></AdminRoute>,
  },
  {
    path: '/admin/evenements',
    element: <AdminRoute><AdminEventJournalPage /></AdminRoute>,
  },

  // ── Console opérateur P4.4 — back-office read-only ─────────────────────────
  {
    path: '/admin',
    element: <AdminRoute><AdminConsoleLayout /></AdminRoute>,
    children: [
      { index: true, element: <AdminDashboardPageP44 /> },
      { path: 'programs', element: <AdminProgramsPage /> },
      { path: 'programs/:programId', element: <AdminProgramsPage /> },
      { path: 'investors', element: <AdminInvestorsPage /> },
      { path: 'investors/:investorId', element: <AdminInvestorsPage /> },
      { path: 'investments', element: <AdminInvestmentsPage /> },
      { path: 'investments/:investmentId', element: <AdminInvestmentsPage /> },
      { path: 'payments', element: <AdminPaymentsPage /> },
      { path: 'payments/:paymentId', element: <AdminPaymentsPage /> },
      { path: 'settlements', element: <AdminSettlementsPage /> },
      { path: 'debt/lots', element: <AdminDebtLotsPage /> },
      { path: 'debt/positions', element: <AdminDebtPositionsPage /> },
      { path: 'debt/ledger', element: <AdminDebtLedgerPage /> },
      { path: 'interest', element: <AdminInterestPage /> },
      { path: 'governance/meetings', element: <AdminGovernanceMeetingsPage /> },
      { path: 'governance/resolutions', element: <AdminGovernanceResolutionsPage /> },
      { path: 'conversions', element: <AdminConversionsPage /> },
      { path: 'conversions/:conversionId', element: <AdminConversionsPage /> },
      { path: 'equity', element: <AdminEquityPage /> },
      { path: 'equity/register', element: <AdminEquityRegisterPage /> },
      { path: 'documents', element: <AdminDocumentsPageP44 /> },
      { path: 'documents/:documentId', element: <AdminDocumentsPageP44 /> },
      { path: 'document-categories', element: <AdminDocumentCategoriesPage /> },
      { path: 'document-templates', element: <AdminDocumentTemplatesPage /> },
      { path: 'document-factory/readiness', element: <DocumentFactoryReadinessPage /> },
      { path: 'document-audit', element: <AdminDocumentAuditPage /> },
      { path: 'document-exports', element: <AdminDocumentExportsPage /> },
      { path: 'notifications', element: <AdminNotificationsPage /> },
      { path: 'notifications/:messageId', element: <AdminNotificationsPage /> },
      { path: 'notification-attempts', element: <AdminNotificationAttemptsPage /> },
      { path: 'notification-dead-letters', element: <AdminNotificationDeadLettersPage /> },
      { path: 'notification-templates', element: <AdminNotificationTemplatesPage /> },
      { path: 'notification-health', element: <AdminNotificationHealthPage /> },
      { path: 'audit', element: <AdminAuditPage /> },
      { path: 'exceptions', element: <AdminExceptionsPage /> },
      { path: 'system/read-models', element: <AdminReadModelsPage /> },
    ],
  },

  // ── 404 ─────────────────────────────────────────────────────────────────────
  { path: '*', element: <NotFoundPage /> },
  ]},
]);

export function AppRouter() {
  return <PageLoader><RouterProvider router={router} /></PageLoader>;
}
